import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type {
  IWorkspaceMembersListQuery,
  IWorkspaceMembersPage,
  WorkspaceService,
} from "@/services/workspace.service";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The workspace-member reads the caching pilot moves onto Query (NG-2546,
 * contract §2):
 *
 * - **R20** `members.bank`: every member of the workspace,
 *   `[slug, uid, 'members', 'bank']`. Reference data: the pickers, filters and
 *   name lookups that read it need names and ids, which rarely change.
 * - **R21** `members.page`: the settings Members table, server-filtered,
 *   sorted and walked by cursor, `[slug, uid, 'members', 'page', {…}]`. Mutable,
 *   because it is the screen where admins edit those rows.
 * - **R22** `member.eligibleRoles`: which roles one member may be given,
 *   `[slug, uid, 'member', mid, 'eligible-roles']`. Access-sensitive: it is the
 *   check a role picker runs before offering a role.
 *
 * R20 and R21 sit under `'members'` so one prefix invalidation reaches the bank
 * and every page variant; R22 sits under `'member', mid` beside the member's
 * other per-member reads. All three are plain idempotent GETs, so all declare
 * `"safe-read"` and `runRead` owns their retries.
 *
 * The definitions take the service rather than reaching for the root store, so
 * a store passes `rootStore.workspaceService` and a test passes a fake.
 */

/** The parameters of R21 that change the response body; the cursor is the page param. */
export type MembersPageQuery = Omit<IWorkspaceMembersListQuery, "cursor">;

/** The R20 key. */
export function memberBankKey(scope: QueryScope) {
  return scopedKey(scope, "members", "bank");
}

/**
 * Whether `queryKey` is an R20 key in any scope. A member id is unique across
 * workspaces, so a member write patches every cached bank without naming one.
 * Built from `memberBankKey` itself, so a change to the key reaches the match.
 */
export function isMemberBankKey(queryKey: readonly unknown[]): boolean {
  const shape = memberBankKey({ tenant: "", principal: "" });
  return (
    queryKey.length === shape.length && shape.every((part, i) => i < 2 || queryKey[i] === part)
  );
}

/**
 * The R21 key. Absent and empty parameters send the same request, so they share
 * an entry; `role` and `tier` are sets, so their order does not split one.
 */
export function membersPageKey(scope: QueryScope, query: MembersPageQuery = {}) {
  return scopedKey(scope, "members", "page", {
    q: query.q ?? "",
    order_by: query.order_by ?? "",
    role: [...(query.role ?? [])].sort(),
    tier: [...(query.tier ?? [])].sort(),
    per_page: query.per_page ?? null,
  });
}

/**
 * Whether `queryKey` is an R21 key in any scope, for any filter and sort. A
 * member write patches every cached page, and cancels every page read in
 * flight, without naming one. Built from `membersPageKey` itself, so a change to the key reaches the match:
 * the scope and the trailing parameters are free, the parts between are not.
 */
export function isMembersPageKey(queryKey: readonly unknown[]): boolean {
  const shape = membersPageKey({ tenant: "", principal: "" });
  return (
    queryKey.length === shape.length &&
    shape.every((part, i) => i < 2 || i === shape.length - 1 || queryKey[i] === part)
  );
}

/** The R22 key. */
export function memberEligibleRolesKey(scope: QueryScope, memberId: string) {
  return scopedKey(scope, "member", memberId, "eligible-roles");
}

/** R20: the workspace's whole member list, the one `WorkspaceMembersStore.fetchMembers` reads today. */
export function memberBankQuery(service: Pick<WorkspaceService, "listMembers">, scope: QueryScope) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: memberBankKey(scope),
    queryFn: scopedRead((request) => service.listMembers(scope.tenant, request), {
      retry: "safe-read",
    }),
  });
}

/**
 * R21: the members table's pages for one filter and sort. The first page asks
 * with no cursor, as `loadMembersPage` does today; each later page asks with
 * the cursor the previous one returned, and there is no next page once the
 * server says so or returns no cursor.
 */
export function membersPageQuery(
  service: Pick<WorkspaceService, "listMembersPage">,
  scope: QueryScope,
  query: MembersPageQuery = {},
) {
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    queryKey: membersPageKey(scope, query),
    initialPageParam: null as string | null,
    queryFn: scopedRead<IWorkspaceMembersPage, string | null>(
      (request, { pageParam }) =>
        service.listMembersPage(scope.tenant, { ...query, cursor: pageParam }, request),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: IWorkspaceMembersPage) =>
      last.next_page_results && last.next_cursor ? last.next_cursor : undefined,
  });
}

/** R22: one member's role eligibility, checked when a role picker opens. */
export function memberEligibleRolesQuery(
  service: Pick<WorkspaceService, "fetchMemberEligibleRoles">,
  scope: QueryScope,
  memberId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: memberEligibleRolesKey(scope, memberId),
    queryFn: scopedRead(
      (request) => service.fetchMemberEligibleRoles(scope.tenant, memberId, request),
      { retry: "safe-read" },
    ),
  });
}
